import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Factory, Package, Link2, User, Calendar, FileText, AlertCircle } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-KE').format(Number(n || 0).toFixed(2));
const fmtKES = (n) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(n || 0);

export default function ProductionDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/productions/${id}`);
        setDetail(res.data);
      } catch (e) {
        toast.error('Failed to load production');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }
  if (!detail) return <div className="p-8 text-center text-slate-500">Production not found.</div>;

  const { production: p, linked_purchase: batch, sibling_productions: siblings } = detail;
  const byproducts = p.byproducts || [];
  const totalByproductValue = byproducts.reduce((s, bp) => s + (bp.total_value || 0), 0);

  return (
    <div className="space-y-6" data-testid="production-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/production"><Button variant="ghost" size="sm" className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button></Link>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-[#1B4D3E] truncate">Production Record</h1>
            <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
              <Calendar className="h-3.5 w-3.5" /> {p.date}
              {p.factory && <><span>·</span><Factory className="h-3.5 w-3.5" /> {p.factory}</>}
              {p.pack_size && <><span>·</span><Package className="h-3.5 w-3.5" /> {p.pack_size}</>}
            </p>
          </div>
        </div>
      </div>

      {/* Batch / Purchase Information */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4 text-emerald-700" /> Batch / Purchase Information</CardTitle></CardHeader>
        <CardContent>
          {!batch ? (
            <div className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span>No purchase batch linked to this production. Edit the production to link a batch.</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Info label="Batch No" value={<Link to={`/batches/${batch.id}`} className="text-emerald-700 hover:underline font-medium">{batch.batch_no}</Link>} />
              <Info label="Supplier" value={batch.supplier_name || '-'} />
              <Info label="Purchase Date" value={batch.date || '-'} />
              <Info label="Kg Purchased" value={`${fmt(batch.kg_received)} kg`} />
              <Info label="Purchase Cost" value={fmtKES(batch.total_cost)} />
              <Info label="Factory" value={batch.factory || '-'} />
            </div>
          )}
          {batch && (
            <div className="mt-4 pt-4 border-t">
              <Link to={`/batches/${batch.id}`}>
                <Button size="sm" className="bg-[#1B4D3E] gap-1" data-testid="open-batch-report-from-production"><FileText className="h-4 w-4" /> Open Batch Report</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Production Information */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Factory className="h-4 w-4" /> Production Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <Info label="Production Date" value={p.date} />
            <Info label="Pack Size" value={p.pack_size || '-'} />
            <Info label="Actual Weight / Packet" value={p.actual_weight_per_packet ? `${fmt(p.actual_weight_per_packet)} kg` : '-'} />
            <Info label="Packets / Bale" value={p.packets_per_bale ? fmt(p.packets_per_bale) : '-'} />
            <Info label="Number of Bales" value={p.num_bales ? fmt(p.num_bales) : '-'} />
            <Info label="Total Packets" value={p.total_packets ? fmt(p.total_packets) : '-'} />
            <Info label="Total Weight Packed" value={p.total_weight_packed ? `${fmt(p.total_weight_packed)} kg` : '-'} />
            <Info label="Packaging Team" value={p.packaging_team_id ? (<Link to={`/packaging-teams/${p.packaging_team_id}/statement`} className="text-emerald-700 hover:underline"><User className="inline h-3.5 w-3.5 mr-1" />{p.packaging_team_name || 'Team'}</Link>) : '-'} />
            <Info label="Packaging Cost / Bale" value={p.packaging_cost_per_bale ? fmtKES(p.packaging_cost_per_bale) : '-'} />
            <Info label="Total Amount Earned" value={p.total_amount_earned ? fmtKES(p.total_amount_earned) : '-'} />
          </div>
          {p.remarks && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Remarks</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.remarks}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Byproducts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2"><Package className="h-4 w-4" /> Byproducts</span>
            <span className="text-sm font-normal text-slate-500">
              Total Qty: <b>{fmt(p.total_byproducts_kg || 0)} kg</b>
              {totalByproductValue > 0 && <> · Total Value: <b className="text-emerald-700">{fmtKES(totalByproductValue)}</b></>}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {byproducts.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No byproducts recorded.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Quantity (kg)</TableHead>
                  <TableHead className="text-right">Unit Price / kg</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byproducts.map((bp, idx) => (
                  <TableRow key={idx}>
                    <TableCell><Badge variant="outline">{bp.name}</Badge></TableCell>
                    <TableCell className="text-right">{fmt(bp.quantity_kg)}</TableCell>
                    <TableCell className="text-right">{bp.unit_price_per_kg ? fmtKES(bp.unit_price_per_kg) : '-'}</TableCell>
                    <TableCell className="text-right font-medium">{bp.total_value ? fmtKES(bp.total_value) : '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600">{bp.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Sibling productions on same batch (foundation for Phase 2 batch report) */}
      {siblings && siblings.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Other Productions on this Batch ({siblings.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Pack Size</TableHead>
                  <TableHead className="text-right">Bales</TableHead>
                  <TableHead className="text-right">Packets</TableHead>
                  <TableHead>Team</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {siblings.map(sp => (
                  <TableRow key={sp.id}>
                    <TableCell><Link to={`/production/${sp.id}`} className="text-emerald-700 hover:underline">{sp.date}</Link></TableCell>
                    <TableCell>{sp.pack_size || '-'}</TableCell>
                    <TableCell className="text-right">{sp.num_bales ? fmt(sp.num_bales) : '-'}</TableCell>
                    <TableCell className="text-right">{sp.total_packets ? fmt(sp.total_packets) : '-'}</TableCell>
                    <TableCell className="text-sm">{sp.packaging_team_name || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value ?? '-'}</p>
    </div>
  );
}
